/**
 * Affiliates (/affiliates): everyone who sends us customers, sorted by the
 * kind of relationship, with the next thing to do for each.
 *
 * Tabs (?tab=, 2026-10-28): People (this page, the default) · Leaderboard ·
 * Payouts · Assets (the swipe library) · Recruit (who to invite next).
 *
 * Relationship (JV partner / pro / creator / casual) sets the follow-up
 * rhythm; the channel (our program / ClickBank / elsewhere) is separate.
 * Program numbers sync nightly from the platform.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Coins, HandCoins, Search, UserPlus, Users, Wallet, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, Input, Select, Skeleton, useToast } from "@/components/ui";
import { Card, Empty, PageHeader, Segmented, Stat, num, usd } from "@/features/command/ui";
import { CoachCard } from "@/features/coach/CoachCard";
import { AddAffiliateModal } from "@/features/affiliates/AddAffiliateModal";
import { AssetsTab } from "@/features/affiliates/AssetsTab";
import { LeaderboardTab } from "@/features/affiliates/LeaderboardTab";
import { PayoutsTab } from "@/features/affiliates/PayoutsTab";
import { RecruitTab } from "@/features/affiliates/RecruitTab";
import { MovesPanel } from "@/features/affiliates/MovesPanel";
import { PeopleList, type RowPatch } from "@/features/affiliates/PeopleList";
import {
  CHANNELS, CHANNEL_FALLBACK, KINDS, KIND_META, RHYTHM_FALLBACK, STATUSES, STATUS_META, kindBlurb, kindLabel,
} from "@/features/affiliates/shared";
import type { AffiliateKind, AffiliatesList, ProgramTab } from "@/features/affiliates/types";

const TABS: Array<{ value: ProgramTab; label: string }> = [
  { value: "people", label: "People" },
  { value: "leaderboard", label: "Leaderboard" },
  { value: "payouts", label: "Payouts" },
  { value: "assets", label: "Assets" },
  { value: "recruit", label: "Recruit" },
];

export function AffiliatesPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<AffiliatesList | null>(null);
  const [movesVersion, setMovesVersion] = useState(0);
  const [adding, setAdding] = useState(false);

  const kind = (params.get("kind") ?? "") as AffiliateKind | "";
  const channel = params.get("channel") ?? "";
  const status = params.get("status") ?? "";
  const q = params.get("q") ?? "";
  const showHidden = params.get("hidden") === "1";
  const tab: ProgramTab = TABS.some((t) => t.value === params.get("tab")) ? (params.get("tab") as ProgramTab) : "people";

  const setParam = useCallback((key: string, value: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: true });
  }, [setParams]);

  /** Tabs go in the history, so Back returns to the last one. */
  const setTab = useCallback((t: ProgramTab) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (t === "people") next.delete("tab"); else next.set("tab", t);
      return next;
    });
  }, [setParams]);

  const load = useCallback(async () => {
    try {
      const d = await api.get<AffiliatesList>("/affiliates", { hidden: showHidden ? 1 : undefined });
      setData(d);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load affiliates");
      setData((prev) => prev ?? { affiliates: [], counts: { partner: 0, pro: 0, creator: 0, casual: 0 }, totals: { referred: 0, gross: 0, due: 0, pending: 0, paid: 0 }, rhythm: RHYTHM_FALLBACK, channels: CHANNEL_FALLBACK });
    }
  }, [showHidden, toast]);

  useEffect(() => { void load(); }, [load]);

  const refreshAll = useCallback(() => { void load(); setMovesVersion((v) => v + 1); }, [load]);

  const patch = useCallback(async (id: string, change: RowPatch) => {
    // Show it straight away; the server answer (and any new rhythm) follows.
    setData((d) => d && { ...d, affiliates: d.affiliates.map((a) => (a.id === id ? { ...a, ...change } : a)) });
    try {
      await api.patch(`/affiliates/${id}`, change);
      refreshAll();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
      void load();
    }
  }, [load, refreshAll, toast]);

  const rows = useMemo(() => {
    const list = data?.affiliates ?? [];
    const needle = q.trim().toLowerCase();
    return list.filter((a) => {
      if (kind && a.kind !== kind) return false;
      if (channel && a.channel !== channel) return false;
      if (status && a.status !== status) return false;
      if (needle) {
        const hay = [a.name, a.email, a.channel_note, ...a.codes, ...(a.handles ?? []).map((h) => h.handle)].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data, kind, channel, status, q]);

  const rhythm = data?.rhythm ?? RHYTHM_FALLBACK;
  const channels = data?.channels ?? CHANNEL_FALLBACK;
  const filtered = Boolean(kind || channel || status || q);
  const totals = data?.totals;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Growth"
        title="Affiliates"
        subtitle="Everyone who sends us customers, and the next thing to do for each"
        actions={<Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setAdding(true)}>Add someone</Button>}
      />

      {/* Smaller on a phone so all five fit. */}
      <div className="-mx-1 overflow-x-auto whitespace-nowrap px-1 pb-0.5">
        <span className="sm:hidden"><Segmented<ProgramTab> size="sm" value={tab} onChange={setTab} options={TABS} /></span>
        <span className="hidden sm:inline"><Segmented<ProgramTab> value={tab} onChange={setTab} options={TABS} /></span>
      </div>

      {tab === "leaderboard" && <LeaderboardTab onTab={setTab} />}
      {tab === "payouts" && <PayoutsTab onReminded={refreshAll} />}
      {tab === "assets" && <AssetsTab />}
      {tab === "recruit" && <RecruitTab onInvited={refreshAll} />}

      {tab === "people" && (
        <>
          <CoachCard area="affiliates" />

          {/* The four kinds of relationship (click one to filter). */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {KINDS.map((k) => {
              const meta = KIND_META[k];
              const Icon = meta.icon;
              const on = kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setParam("kind", on ? null : k)}
                  className="text-left"
                  aria-pressed={on}
                >
                  <Card hover className={cn("h-full p-3.5 sm:p-4", on && "cc-glow")}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                        <Icon className={cn("h-3.5 w-3.5 shrink-0", meta.iconClass)} />
                        <span className="truncate">{meta.plural}</span>
                      </span>
                      {on && <span className="rounded-full bg-indigo-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-200">Showing</span>}
                    </div>
                    <div className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-zinc-50">
                      {data ? num(data.counts?.[k] ?? 0) : <Skeleton className="h-7 w-10" />}
                    </div>
                    <p className="mt-1 text-[11px] leading-snug text-zinc-500">{kindBlurb(k, rhythm)}</p>
                  </Card>
                </button>
              );
            })}
          </div>

          {/* Program totals (everyone not hidden). */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="People referred" icon={Users} value={totals ? num(totals.referred) : <Skeleton className="h-7 w-12" />} sub="Signed up through a link" />
            <Stat label="Earned for them" icon={Coins} value={totals ? usd(totals.gross) : <Skeleton className="h-7 w-16" />} sub="Commission, all time" />
            <Stat
              label="Owed now"
              icon={HandCoins}
              tone={totals && totals.due > 0 ? "brand" : "default"}
              big={Boolean(totals && totals.due > 0)}
              value={totals ? usd(totals.due) : <Skeleton className="h-7 w-16" />}
              sub={totals && totals.pending > 0 ? `Plus ${usd(totals.pending)} still clearing` : "Ready to pay out"}
            />
            <Stat label="Paid out" icon={Wallet} value={totals ? usd(totals.paid) : <Skeleton className="h-7 w-16" />} sub="Already sent to them" />
          </div>

          <MovesPanel version={movesVersion} onChanged={refreshAll} rhythm={rhythm} kind={kind || null} />

          {/* Everyone. */}
          <Card as="section" className="min-w-0 overflow-hidden">
            <div className="flex flex-col gap-3 border-b border-zinc-800/70 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-zinc-100">People</h2>
                {data && <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-300">{rows.length}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                <div className="col-span-2 sm:w-52">
                  <Input
                    value={q}
                    onChange={(e) => setParam("q", e.target.value)}
                    placeholder="Search name, email, code"
                    leftIcon={<Search className="h-3.5 w-3.5" />}
                    className="h-8 text-[13px]"
                  />
                </div>
                <Select value={kind} onChange={(e) => setParam("kind", e.target.value)} className="h-8 text-[13px] sm:w-40" aria-label="Relationship">
                  <option value="">Any relationship</option>
                  {KINDS.map((k) => <option key={k} value={k}>{kindLabel(k, rhythm)}</option>)}
                </Select>
                <Select value={channel} onChange={(e) => setParam("channel", e.target.value)} className="h-8 text-[13px] sm:w-36" aria-label="Channel">
                  <option value="">Any channel</option>
                  {CHANNELS.map((c) => <option key={c} value={c}>{channels[c] ?? CHANNEL_FALLBACK[c]}</option>)}
                </Select>
                <Select value={status} onChange={(e) => setParam("status", e.target.value)} className="h-8 text-[13px] sm:w-32" aria-label="Status">
                  <option value="">Any status</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </Select>
                <label className="flex h-8 cursor-pointer items-center gap-2 rounded-lg px-1 text-[13px] text-zinc-400 hover:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={showHidden}
                    onChange={(e) => setParam("hidden", e.target.checked ? "1" : null)}
                    className="h-3.5 w-3.5 accent-indigo-500"
                  />
                  Show hidden
                </label>
              </div>
            </div>

            {data == null ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-11 w-full rounded-lg" />)}
              </div>
            ) : rows.length === 0 ? (
              filtered ? (
                <Empty
                  icon={Search}
                  title="Nobody matches"
                  action={<Button size="sm" variant="secondary" leftIcon={<X className="h-3.5 w-3.5" />} onClick={() => setParams(showHidden ? { hidden: "1" } : {}, { replace: true })}>Clear filters</Button>}
                >
                  Try a different search, or clear the filters to see everyone.
                </Empty>
              ) : (
                <Empty
                  icon={Users}
                  title="Nobody here yet"
                  action={<Button size="sm" leftIcon={<UserPlus className="h-3.5 w-3.5" />} onClick={() => setAdding(true)}>Add someone</Button>}
                >
                  People in our referral program show up here by themselves every night. Add JV partners, ClickBank sellers and creators by hand.
                </Empty>
              )
            ) : (
              <PeopleList rows={rows} rhythm={rhythm} channels={channels} onPatch={(id, p) => void patch(id, p)} />
            )}
            {data && !showHidden && (
              <div className="border-t border-zinc-800/60 px-4 py-2.5 text-[11px] text-zinc-500">
                Our own team and test accounts are hidden.{" "}
                <button type="button" className="font-medium text-indigo-300 hover:text-indigo-200" onClick={() => setParam("hidden", "1")}>Show them</button>
              </div>
            )}
          </Card>
        </>
      )}

      <AddAffiliateModal
        open={adding}
        onClose={() => setAdding(false)}
        rhythm={rhythm}
        channels={channels}
        initialKind={kind || null}
        onCreated={(a) => {
          setAdding(false);
          refreshAll();
          navigate(`/affiliates/${a.id}`, { state: { from: pathname + search } });
        }}
      />
    </div>
  );
}
